// WMS-IV Logisches Gedächtnis (German) scoring utility.
//
// Two booklets, two scoresheet blocks, two parquet sets:
//   A+B (Geschichten A & B) -- 65-90 PDF -- ages 65-90 -- rows G59/G60/G61
//   B+C (Geschichten B & C) -- 16-69 PDF -- ages 16-69 -- rows G62/G63/G64
//
// Three measures per booklet:
//   lg1_<v>.parquet        Textgedächtnis unmittelbar    raw -> WP
//   lg2_<v>.parquet        Textgedächtnis mittelfristig  raw -> WP
//   lg2_recog_<v>.parquet  Textinhalte wiedererkennen    raw -> PR band string

export const AGE_GROUPS_AB = ["65-69", "70-74", "75-79", "80-84", "85-90"];
export const AGE_GROUPS_BC = [
  "16-17", "18-19", "20-24", "25-29", "30-34", "35-44",
  "45-54", "55-64", "65-69",
];

/** Map age to A+B booklet group, or null if outside 65-90. */
export function getAgeGroupAB(age) {
  if (age == null || age < 65 || age >= 91) return null;
  if (age < 70) return "65-69";
  if (age < 75) return "70-74";
  if (age < 80) return "75-79";
  if (age < 85) return "80-84";
  return "85-90";
}

/** Map age to B+C booklet group, or null if outside 16-69. */
export function getAgeGroupBC(age) {
  if (age == null || age < 16 || age >= 70) return null;
  if (age < 18) return "16-17";
  if (age < 20) return "18-19";
  if (age < 25) return "20-24";
  if (age < 30) return "25-29";
  if (age < 35) return "30-34";
  if (age < 45) return "35-44";
  if (age < 55) return "45-54";
  if (age < 65) return "55-64";
  return "65-69";
}

const _cache = {};
async function loadRows(file) {
  if (_cache[file]) return _cache[file];
  if (!window?.electronAPI?.queryParquet) {
    throw new Error("queryParquet IPC nicht verfügbar");
  }
  const res = await window.electronAPI.queryParquet(`WMS-IV-DE/${file}`);
  if (!res.success) throw new Error(res.error);
  _cache[file] = res.rows;
  return res.rows;
}

// Highest raw_score with a non-null column value (booklet-specific cap).
function maxValidRaw(rows, col) {
  let max = -1;
  for (const r of rows) {
    if (r[col] != null && r.raw_score > max) max = r.raw_score;
  }
  return max;
}

/**
 * Lookup the column value for a clamped raw_score. Raw values above the
 * column cap clamp to the highest valid raw; below 0 clamps to 0.
 */
export function lookup(rows, col, raw) {
  if (!rows || rows.length === 0) return null;
  const cap = maxValidRaw(rows, col);
  if (cap < 0) return null;
  const clamped = Math.max(0, Math.min(raw, cap));
  const row = rows.find((r) => r.raw_score === clamped);
  return row ? row[col] ?? null : null;
}

const RANGE_ERROR = {
  ab: "Alter ausserhalb des Normbereichs (65–90 Jahre, Geschichten A & B)",
  bc: "Alter ausserhalb des Normbereichs (16–69 Jahre, Geschichten B & C)",
};

async function scoreWP(file, raw, age, version, label) {
  const ageGroup = version === "bc" ? getAgeGroupBC(age) : getAgeGroupAB(age);
  if (!ageGroup) return { error: `${label}: ${RANGE_ERROR[version]}` };
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return { error: "Ungültiger Rohwert" };
  const rows = await loadRows(file);
  const wp = lookup(rows, ageGroup, n);
  if (wp == null) return { error: `${label}: keine Normwerte für Altersgruppe ${ageGroup}` };
  return { wp, ageGroup, raw: n };
}

async function scorePR(file, raw, age, version, label) {
  const ageGroup = version === "bc" ? getAgeGroupBC(age) : getAgeGroupAB(age);
  if (!ageGroup) return { error: `${label}: ${RANGE_ERROR[version]}` };
  const n = parseInt(raw, 10);
  if (Number.isNaN(n)) return { error: "Ungültiger Rohwert" };
  const rows = await loadRows(file);
  const pr = lookup(rows, ageGroup, n);
  if (pr == null) return { error: `${label}: keine Normwerte für Altersgruppe ${ageGroup}` };
  return { pr, ageGroup, raw: n };
}

export function scoreLGI(raw, age, version) {
  return scoreWP(`lg1_${version}.parquet`, raw, age, version, "LG I");
}

export function scoreLGII(raw, age, version) {
  return scoreWP(`lg2_${version}.parquet`, raw, age, version, "LG II");
}

export function scoreLGIIRecog(raw, age, version) {
  return scorePR(`lg2_recog_${version}.parquet`, raw, age, version, "LG II Wiedererkennung");
}
